import { useCallback, useState } from 'react';

import Map from 'components/Map';
import {
    DEFAULT_CENTER,
    DEFAULT_MAX_ZOOM,
    DEFAULT_MIN_ZOOM,
    DEFAULT_ZOOM
} from 'components/Map/constants';
import AirspaceSheet from 'components/airspace/AirspaceSheet';
import SearchDrawer from 'components/airspace/SearchDrawer';

import useAirspaceSummary from 'hooks/useAirspaceSummary';
import useAirspaceWeather from 'hooks/useAirspaceWeather';
import useReverseGeocode from 'hooks/useReverseGeocode';

import useMapClick from './hooks/useMapClick';
import useResolvedStyle from './hooks/useResolvedStyle';

import { MAP_STYLE_OPTIONS, TEST_ID } from './constants';
import type {
    AirspaceTheme,
    FlyToTarget,
    FunctionComponent,
    MapStyleId,
    Mode,
    Point,
    Props
} from './types';

import styles from './styles.module.scss';

const mapInteractionOptions = {
    hasDrawingControls: false,
    hasMapControls: true
};

// Drawer theme follows the active map style, NOT the global app theme.
// Hybrid + dark map styles both use the dark drawer palette; only the
// explicit light map style uses the light drawer palette.
const resolveAirspaceTheme = (styleId: MapStyleId): AirspaceTheme =>
    styleId === 'light' ? 'light' : 'dark';

const AirspacePage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const [point, setPoint] = useState<Point | null>(null);
    const [mode, setMode] = useState<Mode>('search');
    const [mapFlyTarget, setMapFlyTarget] = useState<FlyToTarget | null>(null);
    const [mapStyleId, setMapStyleId] = useState<MapStyleId>('dark');
    const { data, fetchSummary, isLoading } = useAirspaceSummary();
    const { data: weather, fetchWeather } = useAirspaceWeather();
    const { address, fetchLocation, region } = useReverseGeocode();

    const loadPoint = async (nextPoint: Point) => {
        await Promise.all([
            fetchSummary(nextPoint),
            fetchLocation(nextPoint),
            fetchWeather(nextPoint)
        ]);
    };

    const { handlePick } = useMapClick({
        onPoint: async nextPoint => {
            setPoint(nextPoint);
            setMode('location');
            await loadPoint(nextPoint);
        }
    });

    const handlePickLocation = async (next: Point) => {
        setPoint(next);
        setMode('location');
        setMapFlyTarget(next);
        await loadPoint(next);
    };

    const handleReturnToSearch = () => {
        setMode('search');
        setPoint(null);
    };

    const handleFlew = useCallback(() => setMapFlyTarget(null), []);

    const activeStyle =
        MAP_STYLE_OPTIONS.find(option => option.id === mapStyleId) ?? MAP_STYLE_OPTIONS[0];
    const resolvedStyleUrl = useResolvedStyle(activeStyle.path);
    const airspaceTheme = resolveAirspaceTheme(mapStyleId);
    const mapOptions = {
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
        center: DEFAULT_CENTER,
        mapStyle: resolvedStyleUrl ?? activeStyle.path,
        maxZoom: DEFAULT_MAX_ZOOM,
        minZoom: DEFAULT_MIN_ZOOM,
        zoom: DEFAULT_ZOOM
    };

    return (
        <div className={styles.airspacePage} data-testid={testId}>
            <div className={styles.mapWrapper}>
                {resolvedStyleUrl ? (
                    <Map
                        flyToPoint={mapFlyTarget}
                        homePoint={point ? { lat: point.lat, lon: point.lng } : null}
                        isPickingPoint
                        key={resolvedStyleUrl}
                        mapInteractionOptions={mapInteractionOptions}
                        mapOptions={mapOptions}
                        onFlew={handleFlew}
                        onPickPoint={handlePick}
                    />
                ) : null}
            </div>

            <div className={styles.styleSwitcher} data-testid='mapStyleSwitcher'>
                {MAP_STYLE_OPTIONS.map(option => {
                    const buttonClass =
                        option.id === mapStyleId ? styles.styleButtonActive : styles.styleButton;

                    return (
                        <button
                            className={buttonClass}
                            data-testid={`mapStyleButton-${option.id}`}
                            key={option.id}
                            onClick={() => setMapStyleId(option.id)}
                            type='button'>
                            {option.label}
                        </button>
                    );
                })}
            </div>

            <div className={styles.sheetWrapper} data-airspace-theme={airspaceTheme}>
                {mode === 'search' ? (
                    <SearchDrawer onPickLocation={handlePickLocation} />
                ) : (
                    <AirspaceSheet
                        addressOverride={address}
                        data={data}
                        isLoading={isLoading}
                        marker={point}
                        onClose={handleReturnToSearch}
                        regionOverride={region}
                        weather={weather}
                    />
                )}
            </div>
        </div>
    );
};

export default AirspacePage;
